import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlayCircle, Tv } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, KeyBadge, BpmBadge } from "@/components/ui-kit";
import ChordViewer from "@/components/ChordViewer";
import { MetronomePanel, PadPanel } from "@/components/MusicTools";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const BPM_ADJ = [
  { l: "-10%", v: 0.9 }, { l: "-5%", v: 0.95 }, { l: "Original", v: 1 }, { l: "+5%", v: 1.05 }, { l: "+10%", v: 1.1 },
];

export default function Practice() {
  const [songs, setSongs] = useState([]);
  const [song, setSong] = useState(null);
  const [adj, setAdj] = useState(1);
  const nav = useNavigate();

  useEffect(() => { api.get("/songs").then((r) => { setSongs(r.data); if (r.data.length) setSong(r.data[0]); }); }, []);
  if (!song) return <PageHeader eyebrow="Woodshed" title="Practice Mode" subtitle="Loading your library…" />;

  const bpm = Math.round((song.bpm || 72) * adj);

  return (
    <div>
      <PageHeader eyebrow="Woodshed" title="Practice Mode"
        subtitle="Pick a song, set your tempo, and rehearse with the chart, metronome and pads together."
        actions={<button onClick={() => nav(`/stage/${song.id}`)} data-testid="practice-stage" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#080C14] border border-[#E5C378]/30 text-[#E5C378] font-semibold text-sm"><Tv className="w-4 h-4" /> Stage Mode</button>} />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select value={song.id} onChange={(e) => setSong(songs.find((s) => s.id === e.target.value))} data-testid="practice-song-select"
          className="bg-[#0B101D] border border-white/10 rounded-xl px-3 py-2 text-sm text-[#F7F5F0]">
          {songs.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select>
        <KeyBadge>{song.arrangement_key}</KeyBadge>
        <BpmBadge>{bpm} BPM</BpmBadge>
        <div className="flex gap-1">
          {BPM_ADJ.map((b) => (
            <button key={b.l} onClick={() => setAdj(b.v)} data-testid={`bpm-adj-${b.l}`}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border ${adj === b.v ? "bg-[#E5C378]/15 text-[#E5C378] border-[#E5C378]/30" : "bg-white/5 text-[#8A97AC] border-white/10"}`}>{b.l}</button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          <Tabs defaultValue="chart">
            <TabsList className="bg-[#0B101D] border border-white/10 mb-4">
              <TabsTrigger value="chart" data-testid="practice-tab-chart">Chart</TabsTrigger>
              <TabsTrigger value="lyrics" data-testid="practice-tab-lyrics">Lyrics</TabsTrigger>
            </TabsList>
            <TabsContent value="chart"><ChordViewer song={song} /></TabsContent>
            <TabsContent value="lyrics">
              <div className="wf-card rounded-2xl p-6 whitespace-pre-wrap text-lg text-[#E7EAF0] leading-relaxed">{song.lyrics || "No lyrics stored yet."}</div>
            </TabsContent>
          </Tabs>
        </div>
        <div className="space-y-6">
          <MetronomePanel key={bpm} initialBpm={bpm} compact />
          <PadPanel defaultKey={song.arrangement_key} />
        </div>
      </div>
    </div>
  );
}
